<template>
    <div class="mobile-navigation">
        <header class="mobile-header">
            <button
                ref="closeButton"
                type="button"
                class="header-button"
                :aria-label="isOpen ? 'Close navigation' : 'Open navigation'"
                aria-controls="mobile-navigation-drawer"
                :aria-expanded="isOpen ? 'true' : 'false'"
                @click="openMenu"
            >
                <X v-if="isOpen" :size="34" :stroke-width="1.8" />
                <Menu v-else :size="30" :stroke-width="1.8" />
            </button>

            <router-link
                to="/"
                class="mobile-logo-link"
                aria-label="Home"
                @click.native="closeMenu"
            >
                <img
                    src="/images/pip-global-safety-logo.svg"
                    alt="PIP Global Safety"
                    class="mobile-logo"
                />
            </router-link>

            <button
                v-if="useProducts"
                type="button"
                class="header-button"
                aria-label="Search"
                data-toggle="modal"
                data-target="#searchPanel"
                @click="closeMenu"
            >
                <SearchIcon :size="32" :stroke-width="2.4" />
            </button>
            <span v-else class="header-spacer" aria-hidden="true"></span>
        </header>

        <div
            class="mobile-menu-overlay"
            :class="{ 'is-open': isOpen }"
            :aria-hidden="!isOpen"
            @click.self="closeMenu"
        >
            <section
                id="mobile-navigation-drawer"
                class="mobile-menu-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Site navigation"
            >
                <div class="drawer-content">
                    <div class="mobile-panel-stage">
                        <div
                            class="mobile-panel main-panel"
                            :class="{
                                'is-active': !activePanel,
                                'is-behind': activePanel,
                            }"
                        >
                            <button
                                v-if="isLoggedIn"
                                type="button"
                                class="account-summary"
                                @click="activePanel = 'account'"
                            >
                                <span>
                                    <small>Welcome,</small>
                                    <strong>{{ userName }}</strong>
                                </span>
                                <ChevronRight :size="26" :stroke-width="1.5" />
                            </button>

                            <nav aria-label="Mobile navigation">
                                <ul class="mobile-menu-list">
                                    <li
                                        v-for="item in mainItems"
                                        :key="item.label"
                                    >
                                        <button
                                            v-if="item.panel"
                                            type="button"
                                            class="menu-row"
                                            @click="activePanel = item.panel"
                                        >
                                            <span>{{ item.label }}</span>
                                            <ChevronRight
                                                :size="26"
                                                :stroke-width="1.5"
                                            />
                                        </button>
                                        <a
                                            v-else-if="item.external"
                                            :href="item.href"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="menu-row"
                                            @click="closeMenu"
                                        >
                                            <span>{{ item.label }}</span>
                                        </a>
                                        <router-link
                                            v-else
                                            :to="item.href"
                                            class="menu-row"
                                            @click.native="closeMenu"
                                        >
                                            <span>{{ item.label }}</span>
                                        </router-link>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <div
                            class="mobile-panel submenu-panel"
                            :class="{ 'is-active': activePanel }"
                        >
                            <Brands
                                v-if="activePanel === 'brands'"
                                :menu-item="brandsMenuItem"
                                mobile
                                @close="activePanel = null"
                                @select="closeMenu"
                            />

                            <template v-else>
                                <button
                                    type="button"
                                    class="panel-back"
                                    @click="activePanel = null"
                                >
                                    <ChevronLeft
                                        :size="27"
                                        :stroke-width="1.5"
                                    />
                                    <span>{{ activePanelTitle }}</span>
                                </button>

                                <ul class="mobile-menu-list submenu-list">
                                    <li
                                        v-for="item in activePanelItems"
                                        :key="item.label"
                                    >
                                        <button
                                            v-if="item.action === 'logout'"
                                            type="button"
                                            class="menu-row"
                                            @click="logout"
                                        >
                                            <span>{{ item.label }}</span>
                                        </button>
                                        <a
                                            v-else-if="item.external"
                                            :href="item.href"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="menu-row"
                                            @click="closeMenu"
                                        >
                                            <span>{{ item.label }}</span>
                                        </a>
                                        <router-link
                                            v-else
                                            :to="item.href"
                                            class="menu-row"
                                            @click.native="closeMenu"
                                        >
                                            <span>{{ item.label }}</span>
                                        </router-link>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </div>

                    <footer
                        class="mobile-socials"
                        aria-label="Social media links"
                    >
                        <a
                            v-for="social in socialLinks"
                            :key="social.label"
                            :href="social.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="social.label"
                        >
                                <font-awesome-icon
                                    :icon="social.icon"
                                    aria-hidden="true"
                                />
                        </a>
                    </footer>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup>
import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import {
    ChevronLeft,
    ChevronRight,
    Menu,
    Search as SearchIcon,
    X,
} from "lucide-vue";
import Brands from "./Brands.vue";

const props = defineProps({
    brandItems: {
        type: Array,
        default: () => [],
    },
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const activePanel = ref(null);
const closeButton = ref(null);
const isOpen = ref(false);

const categories = computed(() => store.state.categories || []);
const isLoggedIn = computed(() => store.state.isLoggedIn);
const useProducts = computed(() => store.state.useProducts);
const user = computed(() => store.state.user || {});

const userName = computed(() => {
    const fullName = [user.value.f_name, user.value.l_name]
        .filter(Boolean)
        .join(" ");
    return fullName || user.value.email || "My Account";
});

const brandsMenuItem = computed(() => ({
    label: "Brands",
    children: props.brandItems,
}));

const productItems = computed(() =>
    categories.value.map((category) => ({
        label: category.select_code.title,
        href: {
            name: "products",
            query: { scID: category.select_code.id },
        },
    }))
);

const accountItems = computed(() => {
    const items = [
        { label: "My Account", href: { name: "myAccount" } },
        { label: "Change Password", href: { name: "changePassword" } },
    ];

    if (user.value.user_group_id === 8) {
        items.push({ label: "Verify Users", href: { name: "verify-user" } });
    }

    if (user.value.approved === "y") {
        items.push(
            { label: "My Tools", href: { name: "myTools" } },
            { label: "Spec Sheet Portal", href: { name: "SpecSheetDownload" } }
        );

        if (![0, 1].includes(Number(user.value.user_group_id))) {
            items.push(
                { label: "Literature Portal", href: { name: "literature-portal" } },
                { label: "Video Portal", href: { name: "video-portal" } }
            );
        }
    }

    items.push({ label: "Log Out", action: "logout" });
    return items;
});

const mainItems = computed(() => [
    ...(useProducts.value ? [{ label: "Products", panel: "products" }] : []),
    { label: "Brands", panel: "brands" },
    { label: "Sales Tools", panel: "sales-tools" },
    { label: "Company", href: "/company" },
    { label: "About Us", panel: "about-us" },
    { label: "Privacy Policy", href: { name: "privacy" } },
]);

const panels = computed(() => ({
    products: {
        title: "Products Category",
        items: productItems.value,
    },
    brands: {
        title: "Brands",
        items: props.brandItems.map((brand) => ({
            label: brand.label,
            href: brand.href,
        })),
    },
    "sales-tools": {
        title: "Sales Tools",
        items: [
            { label: "Learning Center", href: { name: "learning" } },
            { label: "Literature", href: { name: "literature" } },
            { label: "Videos", href: { name: "videos" } },
        ],
    },
    "about-us": {
        title: "About Us",
        items: [
            { label: "Know Us", href: { name: "knowus" } },
            {
                label: "PIP Global",
                href: "https://www.pipglobal.com/",
                external: true,
            },
            { label: "Global Locations", href: { name: "locations" } },
            {
                label: "Career Opportunities",
                href: { name: "careeropportunities" },
            },
            {
                label: "Tradeshow Calendar",
                href: { name: "tradeshowcalendar" },
            },
        ],
    },
    account: {
        title: "My Account",
        items: accountItems.value,
    },
}));

const activePanelTitle = computed(() =>
    activePanel.value ? panels.value[activePanel.value].title : ""
);
const activePanelItems = computed(() =>
    activePanel.value ? panels.value[activePanel.value].items : []
);

const socialLinks = [
    {
        label: "Facebook",
        href: "https://www.facebook.com/WestChesterGear/",
        icon: ["fab", "square-facebook"],
    },
    {
        label: "X",
        href: "https://www.twitter.com/westchestergear",
        icon: ["fab", "square-x-twitter"],
    },
    {
        label: "YouTube",
        href: "https://www.youtube.com/westchestergloves",
        icon: ["fab", "square-youtube"],
    },
    {
        label: "LinkedIn",
        href: "https://www.linkedin.com/company/west-chester-holdings",
        icon: ["fab", "linkedin"],
    },
    {
        label: "Instagram",
        href: "https://www.instagram.com/westchestergear/",
        icon: ["fab", "square-instagram"],
    },
];

function openMenu() {
    isOpen.value = !isOpen.value;
    nextTick(() => closeButton.value && closeButton.value.focus());
}

function closeMenu() {
    isOpen.value = false;
    activePanel.value = null;
}

async function logout() {
    try {
        await window.axios.post("/logout");
    } finally {
        await store.dispatch("logout");
        closeMenu();
        if (proxy.$route.name !== "home") {
            await proxy.$router.push({ name: "home" });
        }
    }
}

function handleKeydown(event) {
    if (event.key === "Escape" && isOpen.value) closeMenu();
}

function handleResize() {
    if (window.innerWidth >= 1200 && isOpen.value) closeMenu();
}

watch(isOpen, (open) => {
    document.body.style.overflow = open ? "hidden" : "";
});

watch(
    () => proxy.$route.fullPath,
    () => closeMenu()
);

onMounted(() => {
    document.addEventListener("keydown", handleKeydown);
    window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("resize", handleResize);
    document.body.style.overflow = "";
});
</script>

<style lang="scss" scoped>
.mobile-navigation {
    display: none;
}

@media (max-width: 1199px) {
    .mobile-navigation {
        display: block;
        position: relative;
        z-index: 11000;
    }

    .mobile-header {
        align-items: center;
        background: #cd1b00;
        display: grid;
        grid-template-columns: 64px 1fr 64px;
        height: 64px;
        position: relative;
        width: 100%;
        z-index: 12001;
    }

    .header-button,
    .mobile-logo-link,
    .header-spacer {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: center;
    }

    .header-button {
        background: transparent;
        border: 0;
        color: white;
        cursor: pointer;
        padding: 0;
    }

    .mobile-logo {
        display: block;
        height: 38px;
        max-width: 130px;
        object-fit: contain;
        width: 100%;
    }

    .mobile-menu-overlay {
        background: transparent;
        bottom: 0;
        left: 0;
        padding: 0;
        position: fixed;
        right: 0;
        top: 64px;
        pointer-events: none;
        visibility: hidden;
        z-index: 12000;
        transition: visibility 0s linear 250ms;

        &.is-open {
            pointer-events: auto;
            visibility: visible;
            transition-delay: 0s;

            .mobile-menu-drawer {
                transform: translateX(0);
            }
        }
    }

    .mobile-menu-drawer {
        background: white;
        height: 100%;
        margin: 0;
        max-width: none;
        overflow: hidden;
        transform: translateX(-100%);
        transition: transform 250ms ease;
        width: 100%;
    }

    .drawer-content {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        padding: 30px 21px 0;
    }

    .mobile-panel-stage {
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
        position: relative;
    }

    .mobile-panel {
        bottom: 0;
        left: 0;
        opacity: 0;
        overflow-y: auto;
        pointer-events: none;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(18px);
        transition: opacity 160ms ease, transform 200ms ease;
        width: 100%;

        &.is-active {
            opacity: 1;
            pointer-events: auto;
            transform: translateX(0);
        }
    }

    .main-panel.is-behind {
        transform: translateX(-18px);
    }

    .account-summary,
    .menu-row,
    .panel-back {
        align-items: center;
        background: transparent;
        border: 0;
        color: #050505;
        display: flex;
        text-align: left;
        text-decoration: none;
        width: 100%;
    }

    .account-summary {
        justify-content: space-between;
        margin-bottom: 20px;
        padding: 4px 0;

        small,
        strong {
            display: block;
        }

        small {
            font-size: 15px;
            font-weight: 400;
            line-height: 1.25;
            text-transform: none;
        }

        strong {
            font-size: 24px;
            line-height: 1.15;
            text-transform: uppercase;
        }
    }

    .mobile-menu-list {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .menu-row {
        font-family: "acumin-pro", Helvetica, Arial, sans-serif !important;
        font-size: 25px;
        font-weight: 700;
        justify-content: space-between;
        line-height: 1.1;
        min-height: 59px;
        padding: 8px 0;
        text-transform: uppercase;

        &:hover,
        &:focus-visible {
            color: #cd1b00;
            text-decoration: none;
        }
    }

    .panel-back {
        cursor: pointer;
        font-family: "acumin-pro-condensed", Helvetica, Arial, sans-serif;
        font-size: 25px;
        font-weight: 700;
        gap: 10px;
        margin-bottom: 8px;
        min-height: 56px;
        padding: 4px 0 12px;
        text-transform: uppercase;
    }

    .submenu-list .menu-row {
        font-size: 20px;
        min-height: 54px;
    }

    .mobile-socials {
        background: transparent !important;
        color: #666;
        display: flex;
        gap: 13px;
        justify-content: center;
        margin-top: auto;
        padding: 0 !important;

        a {
            color: #666;
            font-size: 34px;
            line-height: 1;
            padding: 0;

            &:hover,
            &:focus-visible {
                color: #cd1b00;
            }
        }
    }
}

@media (max-width: 420px) {
    .drawer-content {
        padding-left: 20px;
        padding-right: 20px;
    }

    .menu-row,
    .account-summary strong {
        font-size: 22px;
    }
}

@media (min-width: 768px) and (max-width: 1199px) {
    .mobile-menu-drawer {
        width: 480px;
    }
}
</style>
